import { notNullNorUndefined } from "./notNullNorUndefined";

type UnitKey =
  // 1D
  | "distance"
  | "altitude"
  | "coordinates"
  | "angle"
  // 2D
  | "area"
  | "fieldArea";

type UnitType = { long: string; short: string };
type UnitStructure = {
  decimalPlaces: number;
  metric: UnitType;
  imperial: UnitType;
};
type UnitFormat = Record<UnitKey, UnitStructure>;

/**
 * A standardised way to get the units and formatting for different types of
 * measurements.
 */
const UNIT_FORMAT: UnitFormat = {
  distance: {
    decimalPlaces: 2,
    metric: {
      long: "metres",
      short: "m",
    },
    imperial: {
      long: "feet",
      short: "ft",
    },
  },
  altitude: {
    decimalPlaces: 1,
    metric: {
      long: "metres",
      short: "m",
    },
    imperial: {
      long: "feet",
      short: "ft",
    },
  },
  area: {
    decimalPlaces: 2,
    metric: {
      long: "square metres",
      short: "sqm",
    },
    imperial: {
      long: "acres",
      short: "acre",
    },
  },
  fieldArea: {
    decimalPlaces: 2,
    metric: {
      long: "hectares",
      short: "Ha",
    },
    imperial: {
      long: "",
      short: "",
    },
  },
  coordinates: {
    decimalPlaces: 6,
    metric: {
      long: "degrees",
      short: "deg",
    },
    imperial: {
      long: "degrees",
      short: "deg",
    },
  },
  angle: {
    decimalPlaces: 1,
    metric: {
      long: "degrees",
      short: "deg",
    },
    imperial: {
      long: "degrees",
      short: "deg",
    },
  },
};

type UnitFormatting =
  | "[unit]"
  | " [unit]"
  | "/[unit]"
  | "/ [unit]"
  | " / [unit]"
  | " /[unit]"
  | "([unit])"
  | " ([unit])";

/**
 * A standardised way to get the "per area unit" string
 * This has been wrapped as it is used elsewhere in the code
 */
export const unitArea = (imperialUnits: boolean) =>
  normaliseUnit("area", imperialUnits ? "imperial" : "metric", " / [unit]");

/**
 * Normalises the unit based on the measurement type, unit system, and
 * desired formatting structure.
 */
export const normaliseUnit = (
  measurement: keyof typeof UNIT_FORMAT,
  unitSystem: Exclude<
    keyof (typeof UNIT_FORMAT)[typeof measurement],
    "decimalPlaces"
  >,
  unitFormatting: UnitFormatting = "[unit]",
  unitForm: keyof (typeof UNIT_FORMAT)[typeof measurement][NonNullable<
    typeof unitSystem
  >] = "short"
) =>
  unitFormatting.replace(
    "[unit]",
    UNIT_FORMAT[measurement][unitSystem][unitForm]
  );

/**
 * Normalises the formatting of a given value and it's underlying units
 */
export const normaliseValue = (
  value: number,
  measurement: Parameters<typeof normaliseUnit>[0],
  unitSystem?: Parameters<typeof normaliseUnit>[1],
  unitFormatting: Parameters<typeof normaliseUnit>[2] = "[unit]",
  unitForm: Parameters<typeof normaliseUnit>[3] = "short"
): string => {
  // Round value to specified decimal places
  //
  // Alternative option to using `toFixed`:
  // const roundedValue = roundNumber(
  //   value,
  //   UNIT_FORMAT[measurement].decimalPlaces
  // );
  //
  const roundedValue = value.toFixed(UNIT_FORMAT[measurement].decimalPlaces);
  if (notNullNorUndefined(unitSystem) && notNullNorUndefined(unitForm)) {
    // If `withUnits` is given, add the units and format based on `unitFormat`
    // and using the format structure chosed by `withUnits`
    // NOTE: No space added here, this is controlled by value of
    // `unitFormatting` parameter in `normaliseUnit` call
    return `${roundedValue}${normaliseUnit(
      measurement,
      unitSystem,
      unitFormatting,
      unitForm
    )}`;
  }
  return roundedValue;
};
